import { useState } from 'react';
import '../styles/SetupExamModal.css';

export const COMPULSORY_SECTIONS = [
  {
    id: 'aptitude',
    title: 'Aptitude',
    description: 'Quantitative aptitude, numerical reasoning, and data interpretation',
    questions: 5,
    minutes: 15,
  },
  {
    id: 'logical',
    title: 'Logical Reasoning',
    description: 'Verbal and non-verbal reasoning, critical thinking',
    questions: 10,
    minutes: 15,
  },
];

export const OPTIONAL_SECTIONS = [
  {
    id: 'python',
    title: 'Python',
    description: 'Python programming fundamentals and advanced concepts',
    questions: 10,
    minutes: 15,
  },
  {
    id: 'react',
    title: 'React',
    description: 'React.js fundamentals, hooks, and component patterns',
    questions: 10,
    minutes: 15,
  },
];

const normalizeSection = (section = {}, index = 0) => ({
  id: String(section.id || section.title || section.name || `section-${index + 1}`).trim().toLowerCase(),
  title: section.title || section.name || `Section ${index + 1}`,
  description: section.description || `Assessment questions on ${section.title || section.name || `Section ${index + 1}`}`,
  questions: Number(section.questions ?? section.numQuestions ?? 0),
  minutes: Number(section.minutes ?? section.timeMinutes ?? 0),
  isOptional: Boolean(section.isOptional ?? section.is_optional),
  isCompulsory: Boolean(section.isCompulsory ?? section.is_compulsory),
});

export default function SetupExamModal({ sections = [], minOptional = 1, maxOptional = 3, onClose, onConfirm }) {
  // Support dynamic sections from exam configuration or fall back to static
  const compulsoryList = sections.length > 0
    ? sections.map((section, index) => normalizeSection(section, index)).filter(s => !s.isOptional || s.isCompulsory)
    : COMPULSORY_SECTIONS;

  const optionalList = sections.length > 0
    ? sections.map((section, index) => normalizeSection(section, index)).filter(s => s.isOptional && !s.isCompulsory)
    : OPTIONAL_SECTIONS;

  const [selectedOptional, setSelectedOptional] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const boundedMaxOptional = Math.max(1, Math.min(Number(maxOptional) || optionalList.length || 1, optionalList.length || 1));
  const minimumOptional = optionalList.length > 0
    ? Math.min(Math.max(1, Number(minOptional) || 1), boundedMaxOptional)
    : 0;

  const toggleOptional = (id) => {
    setSelectedOptional((prev) => {
      if (prev.includes(id)) return prev.filter((s) => s !== id);
      if (prev.length >= boundedMaxOptional) return prev;
      return [...prev, id];
    });
  };

  const totalSections = compulsoryList.length + selectedOptional.length;
  const totalMinutes =
    compulsoryList.reduce((acc, s) => acc + s.minutes, 0) +
    selectedOptional.reduce((acc, id) => {
      const s = optionalList.find((o) => o.id === id);
      return acc + (s ? s.minutes : 0);
    }, 0);

  const meetsOptionalRequirement =
    optionalList.length === 0 || (selectedOptional.length >= minimumOptional && selectedOptional.length <= boundedMaxOptional);
  const canConfirm = agreed && meetsOptionalRequirement;

  return (
    <div className="sem-backdrop" onClick={onClose}>
      <div className="sem-modal" onClick={(e) => e.stopPropagation()}>

        {/* Close button — updated SVG */}
        <button className="sem-close" onClick={onClose} aria-label="Close">
          <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g filter="url(#filter0_d_3174_3676)">
              <mask id="path-1-inside-1_3174_3676" fill="white">
                <path d="M2.24121 16.4315C2.24121 8.18177 8.92896 1.49402 17.1787 1.49402C25.4285 1.49402 32.1162 8.18177 32.1162 16.4315C32.1162 24.6813 25.4285 31.369 17.1787 31.369C8.92896 31.369 2.24121 24.6813 2.24121 16.4315Z"/>
              </mask>
              <path d="M2.24121 16.4315C2.24121 8.18177 8.92896 1.49402 17.1787 1.49402C25.4285 1.49402 32.1162 8.18177 32.1162 16.4315C32.1162 24.6813 25.4285 31.369 17.1787 31.369C8.92896 31.369 2.24121 24.6813 2.24121 16.4315Z" fill="white" fillOpacity="0.8" shapeRendering="crispEdges"/>
              <path d="M2.24121 16.4315M32.1162 16.4315M32.1162 16.4315M2.24121 16.4315M17.1787 1.49402M32.1162 16.4315M17.1787 31.369M2.24121 16.4315M17.1787 31.369V30.369C9.48124 30.369 3.24121 24.129 3.24121 16.4315H2.24121H1.24121C1.24121 25.2336 8.37667 32.369 17.1787 32.369V31.369ZM32.1162 16.4315H31.1162C31.1162 24.129 24.8762 30.369 17.1787 30.369V31.369V32.369C25.9807 32.369 33.1162 25.2336 33.1162 16.4315H32.1162ZM17.1787 1.49402V2.49402C24.8762 2.49402 31.1162 8.73405 31.1162 16.4315H32.1162H33.1162C33.1162 7.62948 25.9807 0.494019 17.1787 0.494019V1.49402ZM17.1787 1.49402V0.494019C8.37667 0.494019 1.24121 7.62948 1.24121 16.4315H2.24121H3.24121C3.24121 8.73405 9.48124 2.49402 17.1787 2.49402V1.49402Z" fill="#E5E7EB" mask="url(#path-1-inside-1_3174_3676)"/>
              <path d="M20.9134 12.6974L13.4453 20.1654" stroke="#4A5565" strokeWidth="1.24479" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.4453 12.6974L20.9134 20.1654" stroke="#4A5565" strokeWidth="1.24479" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
            <defs>
              <filter id="filter0_d_3174_3676" x="0.000211" y="1.85966e-05" width="34.357" height="34.357" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="0.747"/>
                <feGaussianBlur stdDeviation="1.1205"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3174_3676"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3174_3676" result="shape"/>
              </filter>
            </defs>
          </svg>
        </button>

        {/* Icon — moved up via reduced top padding on modal */}
        <div className="sem-icon-wrap">
          <div className="sem-icon-ring sem-ring-outer" />
          <div className="sem-icon-ring sem-ring-inner" />
          <div className="sem-icon-circle">
            <div className="sem-icon-inner">
              <svg width="40" height="48" viewBox="0 0 39 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter0_d_3174_3593)">
                  <path d="M15.0837 10.25H12.2503C11.4989 10.25 10.7782 10.5485 10.2469 11.0799C9.7155 11.6112 9.41699 12.3319 9.41699 13.0834V30.0834C9.41699 30.8348 9.7155 31.5555 10.2469 32.0868C10.7782 32.6182 11.4989 32.9167 12.2503 32.9167H26.417C27.1684 32.9167 27.8891 32.6182 28.4205 32.0868C28.9518 31.5555 29.2503 30.8348 29.2503 30.0834V13.0834C29.2503 12.3319 28.9518 11.6112 28.4205 11.0799C27.8891 10.5485 27.1684 10.25 26.417 10.25H23.5837M15.0837 10.25C15.0837 11.0015 15.3822 11.7221 15.9135 12.2535C16.4449 12.7848 17.1655 13.0834 17.917 13.0834H20.7503C21.5018 13.0834 22.2224 12.7848 22.7538 12.2535C23.2851 11.7221 23.5837 11.0015 23.5837 10.25M15.0837 10.25C15.0837 9.49857 15.3822 8.7779 15.9135 8.24655C16.4449 7.7152 17.1655 7.41669 17.917 7.41669H20.7503C21.5018 7.41669 22.2224 7.7152 22.7538 8.24655C23.2851 8.7779 23.5837 9.49857 23.5837 10.25" stroke="white" strokeWidth="2.83333" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15.084 20.167L17.9173 23.0003L23.584 17.3337" stroke="white" strokeWidth="2.83333" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                  <filter id="filter0_d_3174_3593" x="-5.66602" y="-2.83331" width="50" height="50" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset dy="2"/>
                    <feGaussianBlur stdDeviation="4"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3174_3593"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3174_3593" result="shape"/>
                  </filter>
                </defs>
              </svg>
            </div>
          </div>
        </div>

        {/* Title — pushed down slightly */}
        <h2 className="sem-title">Select Your Sections</h2>
        <p className="sem-subtitle">Choose the optional sections you want to attempt</p>

        {/* Scrollable body */}
        <div className="sem-body">

          {/* Compulsory */}
          <div className="sem-section-label">Compulsory Sections (auto-included)</div>
          <div className="sem-section-list">
            {compulsoryList.map((s) => (
              <div key={s.id} className="sem-item sem-item--checked">
                <div className="sem-checkbox sem-checkbox--checked">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="sem-item-info">
                  <span className="sem-item-title">{s.title}</span>
                  <span className="sem-item-desc">{s.description}</span>
                </div>
                <span className="sem-item-meta">{s.questions} questions · {s.minutes} min</span>
              </div>
            ))}
          </div>

          {/* Optional — label and sublabel separated */}
          <div className="sem-section-label" style={{ marginTop: 16 }}>
            Optional Sections
          </div>
          <div className="sem-section-sublabel">
            Select at least {minimumOptional} and up to {boundedMaxOptional} section(s) ({selectedOptional.length} selected)
          </div>

          <div className="sem-section-list">
            {optionalList.map((s) => {
              const isSelected = selectedOptional.includes(s.id);
              const isDisabled = !isSelected && selectedOptional.length >= boundedMaxOptional;
              return (
                <button
                  key={s.id}
                  className={`sem-item sem-item--optional ${isSelected ? 'sem-item--checked' : ''} ${isDisabled ? 'sem-item--disabled' : ''}`}
                  onClick={() => !isDisabled && toggleOptional(s.id)}
                  type="button"
                >
                  <div className={`sem-checkbox ${isSelected ? 'sem-checkbox--checked' : ''}`}>
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className="sem-item-info">
                    <span className="sem-item-title">{s.title}</span>
                    <span className="sem-item-desc">{s.description}</span>
                  </div>
                  <span className="sem-item-meta">{s.questions} questions · {s.minutes} min</span>
                </button>
              );
            })}
          </div>

          {/* Summary footer */}
          <div className="sem-summary">
            <div className="sem-summary-stat">
              <span className="sem-summary-label">Estimated total time</span>
              <span className="sem-summary-value">{totalMinutes} minutes</span>
            </div>
            <div className="sem-summary-stat" style={{ textAlign: 'right' }}>
              <span className="sem-summary-label">Sections selected</span>
              <span className="sem-summary-value">{totalSections}</span>
            </div>
          </div>

          {/* Agreement checkbox */}
          <div className="sem-agree-row">
            <label className="sem-agree-label">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="sem-agree-input"
              />
              <span className="sem-agree-box">{agreed && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}</span>
              I have read and understood all the instructions. I am ready to proceed with the exam.
            </label>
          </div>
        </div>

        {/* Action buttons */}
        <div className="sem-actions">
          <button className="sem-btn sem-btn--cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`sem-btn sem-btn--confirm ${!canConfirm ? 'sem-btn--disabled' : ''}`}
            onClick={() => canConfirm && onConfirm(selectedOptional)}
            disabled={!canConfirm}
          >
            Complete Setup
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7h10M7 2l5 5-5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
